import { Editor } from '@tiptap/core';
import { ActionData } from 'copilot-design-system';
import React from 'react';

interface UseEditorActionsReturn {
  getActionState: (actionId: string) => {
    isActive: boolean;
    isDisabled: boolean;
  };
  onAction: (actionData: ActionData) => void;
}

export const useEditorActions = (
  editor: Editor | null,
  slashMenuRangeRef?: React.MutableRefObject<{ from: number; to: number }>,
): UseEditorActionsReturn => {
  const getActionState = React.useCallback(
    (actionId: string) => {
      // if the action is a heading action, we need to pass
      // the level to the isActive function
      if (actionId.startsWith('heading')) {
        return {
          isActive:
            editor?.isActive('heading', {
              level: parseInt(actionId.split('heading')[1]),
            }) ?? false,
          isDisabled: false,
        };
      }
      // if the action is a text action, we need to pass the text to the isActive function
      if (actionId === 'text') {
        return {
          isActive: editor?.isActive('paragraph') ?? false,
          isDisabled: false,
        };
      }

      return {
        isActive: editor?.isActive(actionId) ?? false,
        isDisabled: false,
      };
    },
    [editor],
  );

  const onAction = React.useCallback(
    (actionData: ActionData) => {
      if (!editor) return;

      const defaultRange = { from: 0, to: 0 };
      const range = slashMenuRangeRef?.current ?? defaultRange;

      switch (actionData.action) {
        case 'bold':
          editor.chain().focus().toggleBold().run();
          break;
        case 'italic':
          editor.chain().focus().toggleItalic().run();
          break;
        case 'underline':
          editor.chain().focus().toggleUnderline().run();
          break;
        case 'paragraph':
          editor.chain().focus().setParagraph().run();
          break;
        case 'heading1':
          editor
            .chain()
            .focus()
            .deleteRange(range)
            .setNode('heading', { level: 1 })
            .run();
          break;
        case 'heading2':
          editor
            .chain()
            .focus()
            .deleteRange(range)
            .setNode('heading', { level: 2 })
            .run();
          break;
        case 'heading3':
          editor
            .chain()
            .focus()
            .deleteRange(range)
            .setNode('heading', { level: 3 })
            .run();
          break;
        case 'bulletList':
          editor.chain().focus().deleteRange(range).toggleBulletList().run();
          break;
        case 'orderedList':
          editor.chain().focus().deleteRange(range).toggleOrderedList().run();
          break;
        default:
          editor.chain().focus().deleteRange(range).run();
          break;
      }
    },
    [editor, slashMenuRangeRef],
  );

  return { getActionState, onAction };
};
